import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import toast from 'react-hot-toast';

type EntityType = 'contact' | 'product' | 'invoice';

interface RefreshOptions {
  system?: 'highlevel' | 'xero';
  entityType?: EntityType;
  runContactMatching?: boolean;
}

const ENTITY_LABELS: Record<EntityType, string> = {
  contact: 'Contacts',
  product: 'Products',
  invoice: 'Invoices',
};

export function useRefreshCounts() {
  const queryClient = useQueryClient();
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  const refreshCountsMutation = useMutation({
    mutationFn: async (options?: RefreshOptions) => {
      return apiClient.refreshEntityCounts(locationId, options);
    },
    onSuccess: (_data, options) => {
      // Invalidate sync status to refresh the dashboard
      queryClient.invalidateQueries({ queryKey: ['sync-status', locationId] });

      let label: string;
      if (options?.entityType) {
        label = ENTITY_LABELS[options.entityType];
      } else if (options?.system) {
        label = options.system === 'highlevel' ? 'HighLevel' : 'Xero';
      } else {
        label = 'All';
      }

      toast.success(`${label} counts refreshed successfully`, {
        icon: '🔄',
        duration: 3000,
      });
    },
    onError: (error: Error, options) => {
      let label: string;
      if (options?.entityType) {
        label = ENTITY_LABELS[options.entityType];
      } else if (options?.system) {
        label = options.system === 'highlevel' ? 'HighLevel' : 'Xero';
      } else {
        label = 'entity';
      }

      toast.error(`Failed to refresh ${label} counts: ${error.message}`, {
        duration: 5000,
      });
    },
  });

  const matchContactsMutation = useMutation({
    mutationFn: async () => {
      return apiClient.matchContacts(locationId);
    },
    onSuccess: (data) => {
      // Invalidate sync status to refresh the dashboard
      queryClient.invalidateQueries({ queryKey: ['sync-status', locationId] });

      toast.success(data.message || `Matched ${data.matchedCount} contacts`, {
        icon: '🔗',
        duration: 5000,
      });
    },
    onError: (error: Error) => {
      toast.error(`Failed to match contacts: ${error.message}`, {
        duration: 5000,
      });
    },
  });

  // Refresh all counts
  const refreshCounts = async (options?: RefreshOptions) => {
    return refreshCountsMutation.mutateAsync(options);
  };

  // Refresh counts for a specific entity type
  const refreshEntityTypeCounts = async (entityType: EntityType) => {
    return refreshCountsMutation.mutateAsync({ entityType });
  };

  // Run contact matching
  const matchContacts = async () => {
    return matchContactsMutation.mutateAsync();
  };

  return {
    refreshCounts,
    refreshEntityTypeCounts,
    matchContacts,
    isLoading: refreshCountsMutation.isPending,
    isMatchingContacts: matchContactsMutation.isPending,
    error: refreshCountsMutation.error,
    matchError: matchContactsMutation.error,
  };
}
